import { ComponentType } from '@angular/cdk/overlay';
import { inject, Injectable } from '@angular/core';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
  MatDialogState,
} from '@angular/material/dialog';

/** A dialog ref plus whether it was already on screen when it was requested. */
export interface ExclusiveDialog<T, R> {
  readonly ref: MatDialogRef<T, R>;
  /**
   * `true` when another caller opened this dialog and is still driving it.
   * `afterClosed()` is multicast, so a second subscriber would re-run the
   * first one's side effect — a reusing caller reads the decision, never
   * repeats the action.
   */
  readonly reused: boolean;
}

/**
 * Opens checkout dialogs under a one-instance-per-identity invariant.
 *
 * Every checkout dialog is `disableClose: true`, so a duplicate instance is not
 * dismissable by the user: it covers the first one and traps the flow. The
 * checkout page can re-run its dialog-opening logic without a state change —
 * a bfcache restore re-fires `pageshow` without re-bootstrapping Angular — so
 * the guard cannot live in the caller's own "already handled" flag.
 */
@Injectable({
  providedIn: 'root',
})
export class CheckoutDialogService {
  readonly #dialog = inject(MatDialog);

  /**
   * Live dialogs by identity. Keyed by the component class by default, or by
   * an explicit key when one component serves several questions.
   */
  readonly #open = new Map<unknown, MatDialogRef<unknown>>();

  /**
   * The open dialog for `key`, or a newly opened one. Callers can subscribe to
   * `afterClosed()` without risking a second instance, and `reused` tells them
   * whether they own the outcome or are only observing someone else's dialog.
   *
   * `key` defaults to the component class. Pass an explicit key when the same
   * component asks different questions: a shell component keyed by type alone
   * would answer a second caller with the first caller's `data` still on
   * screen, silently dropping the new message and crossing both subscribers.
   * The key is not derived from `component.name` — production builds may
   * mangle class names into collisions.
   *
   * When a ref is reused, a `disableClose` request is honored: the same dialog
   * is opened both dismissable (a user-initiated prompt) and locked (a
   * navigation guard whose answer decides whether to release seats), and the
   * stricter of the two has to win.
   */
  openExclusive<T, D = unknown, R = unknown>(
    component: ComponentType<T>,
    config?: MatDialogConfig<D>,
    key?: string,
  ): ExclusiveDialog<T, R> {
    const identity = key ?? component;
    const open = this.#liveDialog(identity) as MatDialogRef<T, R> | undefined;

    if (open) {
      if (config?.disableClose) {
        open.disableClose = true;
      }

      return { ref: open, reused: true };
    }

    const ref = this.#dialog.open<T, D, R>(component, config);
    this.#open.set(identity, ref as MatDialogRef<unknown>);

    return { ref, reused: false };
  }

  /** Whether a dialog for `key` (the component class by default) is open. */
  isOpen<T>(component: ComponentType<T>, key?: string): boolean {
    return this.#liveDialog(key ?? component) !== undefined;
  }

  /**
   * A dialog only counts while it is OPEN. Material keeps a ref in
   * `openDialogs` through its exit animation, and a closing ref replays the
   * previous caller's result to late subscribers — reusing one would answer a
   * navigation guard with a decision the user never made on this pass.
   */
  #liveDialog(identity: unknown): MatDialogRef<unknown> | undefined {
    const ref = this.#open.get(identity);
    if (!ref) return undefined;

    if (ref.getState() !== MatDialogState.OPEN) {
      this.#open.delete(identity);
      return undefined;
    }

    return ref;
  }
}
